import cn from 'clsx'
import { parse, stringify } from 'querystringify'

// Components
import { IconFacebook, IconTwitter } from '@tracklib/kit'

// Hooks
import { useTracking } from 'modules/tracking/hooks'

import styles from './share.css'

const handleShare = (href: string, shareUrl: string, e: React.MouseEvent, callback) => {
  e.preventDefault()
  if (callback) {
    callback()
  }
  const width = 600
  const height = 300
  const top = 100
  const left = window.outerWidth / 2 - width / 2
  const options = `menubar=no,toolbar=no,resizable=yes,scrollbars=yes,top=${top}, left=${left}, height=${height}, width=${width}`
  window.open(href, '', options)
}

type ShareButtonProps = {
  service: string
  buttonClassName: string
  callback: () => void
  text?: string
  campaign?: string
  path: string
  search?: string
  hash?: string
}

export const ShareButton = ({ service, buttonClassName, callback, text, campaign, path, search, hash }: ShareButtonProps): JSX.Element => {
  const trackEvent = useTracking()
  const host = window.location && window.location.origin

  const shareSearchString = stringify({
    ...(search && parse(search)),
    utm_source: service,
    utm_medium: 'social',
    ...(campaign && { utm_campaign: campaign }),
  })

  const shareUrl = `${host}${path}?${shareSearchString}${hash ? `#${hash}` : ''}`

  if (service === 'twitter') {
    const twitterShareUrl = `http://twitter.com/share?url=${encodeURIComponent(shareUrl)}&via=tracklib${
      text ? `&text=${encodeURI(text)}` : ''
    }`
    return (
      <a
        title="Tweet on twitter"
        onClick={e => {
          trackEvent('Social', {
            socialNetwork: service,
            socialAction: 'Tweet',
            socialTarget: shareUrl,
            campaign,
          })
          return handleShare(twitterShareUrl, shareUrl, e, callback)
        }}
        href="#twitter"
        className={cn(styles.twitter, buttonClassName)}
      >
        <IconTwitter />
        <span>Tweet</span>
      </a>
    )
  }

  if (service === 'facebook') {
    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`
    return (
      <a
        title="Share on Facebook"
        onClick={e => {
          trackEvent('Social', {
            socialNetwork: service,
            socialAction: 'Share',
            socialTarget: shareUrl,
            campaign,
          })
          return handleShare(fbShareUrl, encodeURIComponent(shareUrl), e, callback)
        }}
        href="#facebook"
        className={cn(styles.facebook, buttonClassName)}
      >
        <IconFacebook />
        <span>Share</span>
      </a>
    )
  }

  return <div />
}

type ShareProps = {
  className?: string
  buttonClassName?: string
  title?: string | null | undefined
  services?: Array<string>
  callback?: () => void
  campaign?: string
  path: string
  search: string
  hash: string
}

const Share = ({
  className,
  buttonClassName,
  title,
  services = ['facebook', 'twitter'],
  callback,
  campaign,
  path,
  search,
  hash,
}: ShareProps): JSX.Element => (
  <div className={cn(className)}>
    {title && <p className={styles.title}>{title}</p>}
    <ul className={styles.list} datatesting="share_list">
      {services?.map(x => (
        <li key={x}>
          <ShareButton
            service={x}
            buttonClassName={buttonClassName}
            callback={callback}
            campaign={campaign}
            path={path}
            search={search}
            hash={hash}
          />
        </li>
      ))}
    </ul>
  </div>
)

export default Share
